/**
 * lib/responsive.ts · single source of truth for responsive behavior
 *
 * Every screen and component derives its breakpoint state, device class,
 * responsive padding and content width from here. Do NOT recompute
 * `width < 820` or read `useWindowDimensions` / `Dimensions.get` for
 * layout decisions anywhere else — import from this file instead.
 *
 * API (intentionally tiny):
 *   BP        — breakpoint edges (mobile / tablet / desktop)
 *   CONTENT   — max content widths (form / narrow / default / wide)
 *   isWeb     — Platform.OS === 'web'
 *   useResponsive()  — reactive hook → { width, height, device, isMobile,
 *                      isTablet, isDesktop, isWeb, pad, select() }
 *   getViewport()    — non-reactive snapshot for code outside React render
 *                      (e.g. one-off measurements in dialogs)
 */
import { Platform, useWindowDimensions, Dimensions } from 'react-native';

/**
 * Breakpoint edges (px). `mobile` is the long-standing app threshold below
 * which the docked sidebar becomes an overlay drawer and rows stack.
 *   mobile  : width < 820            → phones + small tablets (portrait)
 *   tablet  : 820 <= width < 1024    → large tablets / small laptops
 *   desktop : width >= 1024          → laptops / desktop / wide web
 */
export const BP = { mobile: 820, tablet: 1024, desktop: 1280 } as const;

/** Max content widths — centralized so screens stop hardcoding 480/900/1000/1400. */
export const CONTENT = {
  form: 480, // auth + single-column forms
  narrow: 720, // focused reading / settings
  default: 1000, // standard screens
  wide: 1400, // dense tables / dashboards
} as const;

export const isWeb = Platform.OS === 'web';

export type DeviceClass = 'mobile' | 'tablet' | 'desktop';

/** Pick a value per device class. `mobile` is required; others fall back down. */
export interface SelectMap<T> {
  mobile: T;
  tablet?: T;
  desktop?: T;
}

function classify(width: number): DeviceClass {
  if (width < BP.mobile) return 'mobile';
  if (width < BP.tablet) return 'tablet';
  return 'desktop';
}

function pick<T>(device: DeviceClass, map: SelectMap<T>): T {
  if (device === 'desktop') return map.desktop ?? map.tablet ?? map.mobile;
  if (device === 'tablet') return map.tablet ?? map.mobile;
  return map.mobile;
}

export interface Responsive {
  width: number;
  height: number;
  device: DeviceClass;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWeb: boolean;
  /** Standard horizontal screen padding for the current device. */
  pad: number;
  /** Choose a value by device class, e.g. select({ mobile: 16, desktop: 28 }). */
  select<T>(map: SelectMap<T>): T;
}

function build(width: number, height: number): Responsive {
  const device = classify(width);
  return {
    width,
    height,
    device,
    isMobile: device === 'mobile',
    isTablet: device === 'tablet',
    isDesktop: device === 'desktop',
    isWeb,
    pad: device === 'mobile' ? 16 : 28,
    select: (map) => pick(device, map),
  };
}

/** Reactive responsive state — re-renders on viewport changes. Use in components. */
export function useResponsive(): Responsive {
  const { width, height } = useWindowDimensions();
  return build(width, height);
}

/**
 * Non-reactive snapshot for code paths that run outside React's render flow
 * (e.g. computing a one-off modal width). Prefer useResponsive() in components.
 */
export function getViewport(): Responsive {
  const { width, height } = Dimensions.get('window');
  return build(width, height);
}
